import React, { useEffect, useState } from "react";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Flex";
import * as Button from "../../../../../../../common-deprecated/styles/v2/lexus/components/Button";
import Preloader, * as PreloaderStyles from "../../../../../../../common-deprecated/styles/v2/lexus/custom/Preloader";
import {
    IconWrapper,
    ToggleIconWrapper,
} from "../../../../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import Icon from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import { BnbLabel, useBnbLabel } from "../../../../utils/labels";
import Collapsable from "../../common/collapsable/Collapsable";
import * as FinanceStyles from "./styles/Finance";
import type { FinanceInsuranceCalculatorType } from "../../../../utils/FinanceInsuranceCalculatorLink";
import { INSURANCE_CALCULATOR } from "../../../../../../../common-deprecated/constants";
import FinanceInsuranceCalculator from "../../../common/FinanceInsuranceCalculator";
import { useBnbSelector } from "../../../../redux/store";
import { setInsuranceCollapsable } from "../../../../redux/actions/FinanceInsuranceActions";
import { MODAL_FINANCE_DISCLAIMER } from "../../../../../../../common-deprecated/utils/modalConstants";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import InsurancePrice from "../../common/price/InsurancePrice";
import { hasInsuranceConfigured, showFinanceInsurance } from "../../../../utils/financeInsuranceUtils";
import { trackClickedLink, trackInPageCalculator } from "../../../../utils/tracking";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

type PropsType = {
    calculatorType?: FinanceInsuranceCalculatorType;
    expandedByDefault?: boolean; // When provided the finance section is expanded by default
    showIntroduction?: boolean;
};
const Insurance = ({
    calculatorType = INSURANCE_CALCULATOR,
    expandedByDefault = false,
    showIntroduction = true,
}: PropsType): JSX.Element | null => {
    const { fetching, collapsableActive, isPersonalizedQuote, info } = useBnbSelector((state) => state.insuranceRate);
    const { commonSettings, bnbSettings, car } = useBnbSelector((state) => state);
    const { requireDefaultInsurance } = useBnbSelector((state) => state.bnbSettings);
    const step = useBnbSelector((state) => state.navigation.step);
    const finance = useBnbSelector((state) => state.finance);
    const [insuranceDetailsExpanded, setInsuranceDetailsExpanded] = useState<boolean>(false);
    const dispatch = useDispatch();

    const insuranceIsSet = hasInsuranceConfigured(requireDefaultInsurance, isPersonalizedQuote);

    const insuranceCollapsableTitleLabel = useBnbLabel("insuranceCollapsableTitle");
    const insuranceButtonEditLabel = useBnbLabel("insuranceButtonEdit");
    const insuranceCollapseCtaLabel = useBnbLabel("insuranceCollapsableCta");

    useEffect(() => {
        if (expandedByDefault && !collapsableActive) {
            dispatch(setInsuranceCollapsable(true));
        }
    }, []);

    if (
        !showFinanceInsurance({
            type: "insurance",
            bnbSettings,
            car,
            commonSettings,
            finance,
            step,
        })
    ) {
        return null;
    }

    // TRIGGERS AFTER THE COLLAPSABLE HAS EXPANDED/CLOSED
    const onChange = (expanded: boolean): void => {
        dispatch(setInsuranceCollapsable(expanded));
    };

    // Only Triggers after the collapsable button is clicked to open/close the collapsable
    const onTrack = (): void => {
        const trackingLabel = !collapsableActive
            ? "open-inpage-insurance-calculator"
            : "close-inpage-insurance-calculator";
        trackInPageCalculator(!collapsableActive ? "cc-cta" : "cc-action", trackingLabel, step);
    };

    return (
        <FinanceStyles.Wrapper id="insurance-injection-container">
            {showIntroduction && (
                <>
                    <FinanceStyles.Title>
                        <BnbLabel label="insuranceInPageTitle" />
                    </FinanceStyles.Title>
                    <FinanceStyles.Description>
                        <BnbLabel label="insuranceInPageText" useHTML />
                    </FinanceStyles.Description>
                    <FinanceStyles.BenefitsContainer
                        onToggle={() => {
                            setInsuranceDetailsExpanded(!insuranceDetailsExpanded);
                            if (!insuranceDetailsExpanded) {
                                trackClickedLink("why-insurance");
                            }
                        }}
                    >
                        <FinanceStyles.BenefitsHeader>
                            <BnbLabel label="insuranceInPageDetailsLabel" />
                            <ToggleIconWrapper isDown={insuranceDetailsExpanded}>
                                <Icon variant="chevronDown" />
                            </ToggleIconWrapper>
                        </FinanceStyles.BenefitsHeader>
                        <FinanceStyles.BenefitsContent>
                            <BnbLabel label="insuranceInPageDetails" useHTML />
                        </FinanceStyles.BenefitsContent>
                    </FinanceStyles.BenefitsContainer>
                </>
            )}
            <Collapsable
                headerTitle={insuranceCollapsableTitleLabel}
                headerDescription={
                    insuranceIsSet ? (
                        <Flex.Row>
                            <Flex.Col pr={0}>
                                <div>
                                    <InsurancePrice forceHideDisclaimer />
                                </div>
                            </Flex.Col>
                            {info && info.disclaimer && (
                                <Flex.Col p={0}>
                                    <Button.Link
                                        type="button"
                                        hasIconRight
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            if (info?.disclaimer) {
                                                dispatch(
                                                    showModal({
                                                        type: MODAL_FINANCE_DISCLAIMER,
                                                        disclaimer: info.disclaimer,
                                                        productName: "",
                                                    }),
                                                );
                                            }
                                        }}
                                    >
                                        <IconWrapper>
                                            <Icon variant="info" />
                                        </IconWrapper>
                                    </Button.Link>
                                </Flex.Col>
                            )}
                        </Flex.Row>
                    ) : undefined
                }
                ctaLabel={isPersonalizedQuote ? insuranceButtonEditLabel : insuranceCollapseCtaLabel}
                isPersonalized={isPersonalizedQuote}
                icon={<Icon variant="insurance" size="medium" />}
                content={
                    <FinanceInsuranceCalculator
                        type={calculatorType}
                        visible={collapsableActive}
                        showLoader={fetching}
                        loader={
                            <PreloaderStyles.PreLoaderWrapper hasBorder hasButtonHeight>
                                <Preloader size={30} />
                            </PreloaderStyles.PreLoaderWrapper>
                        }
                    />
                }
                active={collapsableActive}
                onChange={onChange}
                onTrack={onTrack}
            />
        </FinanceStyles.Wrapper>
    );
};

export default Insurance;
